import api from "../../services/api";
import type {
  GetMasterStatisticsResponse,
  GetTournamentStatisticsResponse,
  EnableTournamentStatisticsRequest,
  EnableTournamentStatisticsResponse,
  GetPlayerLeaderboardResponse,
  GetPlayerLeaderboardParams,
  GetClubLeaderboardResponse,
  GetClubLeaderboardParams,
  AnalyticsType,
  GetAnalyticsResponse,
  GoldenAwardType,
  GetGoldenAwardResponse,
  GetRankedPlayerLeaderboardResponse,
  GetRankedPlayerLeaderboardParams,
  GetAveragesResponse,
  GetTrendResponse,
  GetTrendParams,
  TeamHighlightType,
  GetTeamHighlightResponse,
  GetClubMatchStatsResponse,
  GetClubStandingsResponse,
  GetMatchPlayerStatisticsResponse,
  GetMatchPlayerStatisticsParams,
} from "./types";

export const statisticsApi = {
  getMasterStatistics: async (): Promise<GetMasterStatisticsResponse> => {
    return api.get("/statistics/master");
  },

  getTournamentStatistics: async (tournamentId: string): Promise<GetTournamentStatisticsResponse> => {
    return api.get(`/statistics/tournament/${tournamentId}`);
  },

  enableTournamentStatistics: async (data: EnableTournamentStatisticsRequest): Promise<EnableTournamentStatisticsResponse> => {
    return api.post("/statistics/tournament/enable", data);
  },

  getPlayerLeaderboard: async (params: GetPlayerLeaderboardParams): Promise<GetPlayerLeaderboardResponse> => {
    const query = new URLSearchParams({ tournamentId: params.tournamentId });
    if (params.statisticIds) {
      query.set("statisticIds", params.statisticIds);
    }
    return api.get(`/statistics/player/leaderboard?${query.toString()}`);
  },

  getClubLeaderboard: async (params: GetClubLeaderboardParams): Promise<GetClubLeaderboardResponse> => {
    const query = new URLSearchParams({ tournamentId: params.tournamentId });
    if (params.statisticIds) {
      query.set("statisticIds", params.statisticIds);
    }
    return api.get(`/statistics/club/leaderboard?${query.toString()}`);
  },

  getAnalytics: async (tournamentId: string, type: AnalyticsType): Promise<GetAnalyticsResponse> => {
    return api.get(`/statistics/insights/${tournamentId}/analytics?type=${type}`);
  },

  getGoldenAward: async (tournamentId: string, type: GoldenAwardType): Promise<GetGoldenAwardResponse> => {
    return api.get(`/statistics/insights/${tournamentId}/golden-award?type=${type}`);
  },

  getRankedPlayerLeaderboard: async (params: GetRankedPlayerLeaderboardParams): Promise<GetRankedPlayerLeaderboardResponse> => {
    const query = new URLSearchParams({
      statisticId: String(params.statisticId),
    });
    if (params.order) {
      query.set("order", params.order);
    }
    return api.get(`/statistics/insights/${params.tournamentId}/leaderboard/players?${query.toString()}`);
  },

  getAverages: async (tournamentId: string): Promise<GetAveragesResponse> => {
    return api.get(`/statistics/insights/${tournamentId}/averages`);
  },

  getTeamHighlight: async (tournamentId: string, type: TeamHighlightType): Promise<GetTeamHighlightResponse> => {
    return api.get(`/statistics/insights/${tournamentId}/team-highlight?type=${type}`);
  },

  getClubMatchStats: async (tournamentId: string): Promise<GetClubMatchStatsResponse> => {
    return api.get(`/statistics/club/match-stats/${tournamentId}`);
  },

  getClubStandings: async (tournamentId: string): Promise<GetClubStandingsResponse> => {
    return api.get(`/statistics/club/standings/${tournamentId}`);
  },

  /**
   * Every player_statistics row recorded for one match, flat.
   * The match detail tabs pivot this into a per-player table and aggregate it by
   * club for the team comparison, so no separate team-totals call is needed.
   */
  getMatchPlayerStatistics: async (
    params: GetMatchPlayerStatisticsParams
  ): Promise<GetMatchPlayerStatisticsResponse> => {
    const query = new URLSearchParams({ matchId: params.matchId });
    if (params.teamId) {
      query.set("teamId", params.teamId);
    }
    const response: GetMatchPlayerStatisticsResponse = await api.get(
      `/statistics/players/match?${query.toString()}`
    );
    /* The response is `{ players: [...] }`, not a bare array. Unwrapped
       defensively so a shape change is an empty tab, not a crash. */
    return { players: Array.isArray(response?.players) ? response.players : [] };
  },

  getTrend: async (params: GetTrendParams): Promise<GetTrendResponse> => {
    const query = new URLSearchParams({
      scope: params.scope,
      statisticIds: params.statisticIds,
    });
    if (params.playerId) {
      query.set("playerId", params.playerId);
    }
    if (params.teamId) {
      query.set("teamId", params.teamId);
    }
    return api.get(`/statistics/insights/${params.tournamentId}/trend?${query.toString()}`);
  },
};
